import React from "react";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";

export interface ActionButtonItem {
  id: string;
  tooltip: string;
  icon: string;
  className?: string;
  as: "link" | "button";
  href?: string;
  onClick?: () => void;
}

export interface TableActionsConfig {
  viewHref?: string;
  editHref?: string;
  /** When set (without editHref), Edit is a button that calls this handler. */
  onEdit?: () => void;
  onDelete?: () => void;
}

const ACTION_VIEW: Omit<ActionButtonItem, "href" | "onClick"> = {
  id: "view",
  tooltip: "View",
  icon: "ri-eye-fill",
  className: "btn btn-link btn-sm p-0 text-primary",
  as: "link",
};
const ACTION_EDIT: Omit<ActionButtonItem, "href" | "onClick"> = {
  id: "edit",
  tooltip: "Edit",
  icon: "ri-pencil-fill",
  className: "btn btn-link btn-sm p-0 text-primary",
  as: "link",
};
const ACTION_DELETE: Omit<ActionButtonItem, "href" | "onClick"> = {
  id: "delete",
  tooltip: "Delete",
  icon: "ri-delete-bin-5-fill",
  className: "btn btn-link btn-sm p-0 text-danger",
  as: "button",
};

/**
 * Build standard View / Edit / Delete actions for table rows.
 * Omit viewHref or editHref to hide that action; omit onDelete to hide delete.
 */
export function buildTableActions(
  config: TableActionsConfig,
): ActionButtonItem[] {
  const actions: ActionButtonItem[] = [];

  if (config.viewHref) {
    actions.push({
      ...ACTION_VIEW,
      href: config.viewHref,
    });
  }
  if (config.editHref) {
    actions.push({
      ...ACTION_EDIT,
      href: config.editHref,
    });
  } else if (config.onEdit) {
    actions.push({
      ...ACTION_EDIT,
      as: "button",
      onClick: config.onEdit,
    });
  }
  if (config.onDelete) {
    actions.push({
      ...ACTION_DELETE,
      onClick: config.onDelete,
    });
  }

  return actions;
}

interface ActionButtonsProps {
  /** Unique prefix for tooltip targets (e.g. row id) to avoid duplicate ids in tables */
  idPrefix: string;
  actions: ActionButtonItem[];
  listClassName?: string;
  itemClassName?: string;
}

const ActionButtons = ({
  idPrefix,
  actions,
  listClassName = "list-inline hstack gap-2 mb-0",
  itemClassName = "list-inline-item",
}: ActionButtonsProps) => {
  if (actions.length === 0) return null;

  return (
    <ul className={listClassName}>
      {actions.map((action) => {
        const targetId = `${idPrefix}-${action.id}`;
        const content = <i className={`${action.icon} fs-16`}></i>;
        const className =
          action.className ??
          "btn btn-link btn-sm p-0 text-primary d-inline-block";

        return (
          <li key={action.id} className={itemClassName}>
            {action.as === "link" && action.href ? (
              <Link
                id={targetId}
                to={action.href}
                className={className}
                onClick={action.onClick}
                aria-label={action.tooltip}
              >
                {content}
              </Link>
            ) : (
              <button
                id={targetId}
                type="button"
                className={className}
                onClick={action.onClick}
                aria-label={action.tooltip}
              >
                {content}
              </button>
            )}
            <UncontrolledTooltip target={targetId} placement="top">
              {action.tooltip}
            </UncontrolledTooltip>
          </li>
        );
      })}
    </ul>
  );
};

export default ActionButtons;
